import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  DndContext, KeyboardSensor, PointerSensor, TouchSensor,
  closestCenter, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, arrayMove, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAuth } from './AuthContext';
import { useConfirm } from './ConfirmDialog';
import 'bootstrap/dist/css/bootstrap.min.css';


// Always return a list: an error body (e.g. {detail: "Not authenticated"})
// reaching a .map() call would blank the whole page.
const fetchList = async (url, options) => {
  const res = await fetch(url, options);
  if (!res.ok) {
    if (res.status === 401 || res.status === 403) throw new Error('Your session expired. Please log in again.');
    throw new Error('Request failed.');
  }
  const data = await res.json();
  return Array.isArray(data) ? data : [];
};

const UNCATEGORIZED = 'Uncategorized';

// Drag handles are separate from the row so the Edit/Delete buttons stay
// clickable and the list still works without dragging.
const DragHandle = ({ label, ...props }) => (
  <span
    {...props}
    role="button"
    aria-label={label}
    title={label}
    style={{ cursor: 'grab', touchAction: 'none', color: '#9c8c7c', fontSize: 18, lineHeight: 1, padding: '0 4px' }}
  >
    ⠿
  </span>
);

const SortableRow = ({ id, data, children, className, style: extraStyle }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, data });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    ...extraStyle,
  };
  return (
    <div ref={setNodeRef} style={style} className={className}>
      {children({ handleProps: { ...attributes, ...listeners } })}
    </div>
  );
};

const actionColor = a => (a === 'delete' ? 'danger' : a === 'edit' ? 'warning' : 'success');
const actionLabel = a => (a === 'add' ? 'Add Item' : a === 'edit' ? 'Edit Item' : 'Delete Item');
const itemLabel = item => `${item.size ? item.size + ' ' : ''}${item.foodName} — $${item.price.toFixed(2)}`;

// ── Image picker ───────────────────────────────────────────────────────────────
// imageUrl     – the current saved/chosen URL (null = none)
// imageFile    – a newly selected File object (null = none)
// pendingDelete – true if the user wants to remove the current image on save
// onFileSelect(file)  – user picked a file to upload
// onUrlSelect(url)    – user copied an existing URL
// onDelete()          – user clicked "Remove image"
// onUndoDelete()      – user clicked "Undo" on a pending delete

const ImagePicker = ({ imageUrl, imageFile, pendingDelete, onFileSelect, onUrlSelect, onDelete, onUndoDelete, menuTypes }) => {
  const [mode, setMode] = useState('upload');
  const [pickerMenu, setPickerMenu] = useState('');
  const [pickerItems, setPickerItems] = useState([]);
  const [loadingPicker, setLoadingPicker] = useState(false);
  const [localPreview, setLocalPreview] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!imageFile) { setLocalPreview(null); return; }
    const url = URL.createObjectURL(imageFile);
    setLocalPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  const displayUrl = localPreview || imageUrl;

  const handleFileChange = e => {
    const file = e.target.files[0];
    if (file) onFileSelect(file);
    e.target.value = '';
  };

  const handleMenuChange = async menu => {
    setPickerMenu(menu);
    setPickerItems([]);
    if (!menu) return;
    setLoadingPicker(true);
    try {
      const data = await fetchList(
        `https://api.reshipesbakery.com/item/getItemsWithSpecificMenu?menuName=${encodeURIComponent(menu)}`
      );
      setPickerItems(data.filter(i => i.imageUrl));
    } catch {
      setPickerItems([]);
    } finally {
      setLoadingPicker(false);
    }
  };

  return (
    <div className="form-group mb-3">
      <label className="fw-semibold d-block mb-2">
        Image <small className="text-muted fw-normal">(optional)</small>
      </label>

      {/* Pending delete notice */}
      {pendingDelete && (
        <div className="alert alert-warning py-2 mb-2">
          Image will be deleted when you save.{' '}
          <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={onUndoDelete}>
            Undo
          </button>
        </div>
      )}

      {/* Preview */}
      {displayUrl && !pendingDelete && (
        <div className="d-flex align-items-center gap-3 mb-2">
          <img
            src={displayUrl}
            alt="preview"
            style={{ height: 80, borderRadius: 8, objectFit: 'cover', border: '1px solid #dee2e6' }}
          />
          <div>
            {imageFile && (
              <div className="text-muted small mb-1">New image selected — saves on submit</div>
            )}
            <button type="button" className="btn btn-link btn-sm text-danger p-0" onClick={onDelete}>
              Remove image
            </button>
          </div>
        </div>
      )}

      {/* Mode tabs (hidden while pending delete) */}
      {!pendingDelete && (
        <>
          <div className="btn-group btn-group-sm mb-2" role="group">
            <button
              type="button"
              className={`btn ${mode === 'upload' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setMode('upload')}
            >
              Upload Image
            </button>
            <button
              type="button"
              className={`btn ${mode === 'existing' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setMode('existing')}
            >
              Copy from existing item
            </button>
          </div>

          {mode === 'upload' && (
            <div>
              <input
                ref={fileInputRef}
                type="file"
                className="form-control"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handleFileChange}
              />
              <small className="text-muted">JPEG, PNG, WebP, or GIF · max 5 MB</small>
            </div>
          )}

          {mode === 'existing' && (
            <>
              <select
                className="form-control mb-2"
                value={pickerMenu}
                onChange={e => handleMenuChange(e.target.value)}
              >
                <option value="">— Select a menu —</option>
                {menuTypes.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
              {loadingPicker && <p className="small text-muted">Loading…</p>}
              {!loadingPicker && pickerMenu && pickerItems.length === 0 && (
                <p className="small text-muted">No items with pictures in this menu.</p>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {pickerItems.map(item => (
                  <div
                    key={item.id}
                    className="d-flex align-items-center gap-2 border rounded p-2"
                    style={{ cursor: 'pointer' }}
                    onClick={() => { onUrlSelect(item.imageUrl); setMode('upload'); }}
                  >
                    <img
                      src={item.imageUrl}
                      alt={item.foodName}
                      style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }}
                    />
                    <span className="small">{item.size} {item.foodName}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────
const ManageMenu = () => {
  const { token } = useAuth();
  const [confirm, confirmDialog] = useConfirm();

  const [menuTypes, setMenuTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedMenu, setSelectedMenu] = useState('');
  const [menuItems, setMenuItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [action, setAction] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  // A menu is just a name shared by items, so a brand-new one only exists
  // locally until its first item is saved.
  const [draftMenus, setDraftMenus] = useState([]);
  const [creatingMenu, setCreatingMenu] = useState(false);
  const [newMenuName, setNewMenuName] = useState('');
  const [menuError, setMenuError] = useState(null);
  // Selecting a menu resets the current action; this survives that reset so a
  // freshly created menu opens straight into the Add Item form.
  const pendingAction = useRef(null);

  const allMenus = useMemo(
    () => [...menuTypes, ...draftMenus.filter(d => !menuTypes.includes(d))],
    [menuTypes, draftMenus]
  );
  const isDraftMenu = draftMenus.includes(selectedMenu) && !menuTypes.includes(selectedMenu);

  const [addForm, setAddForm] = useState({
    foodName: '', size: '', price: '', category: '', minQty: '1',
    imageUrl: null, imageFile: null,
  });
  const [editForm, setEditForm] = useState({
    foodName: '', size: '', price: '', category: '', minQty: '1',
    imageUrl: null, imageFile: null, imagePendingDelete: false,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [orderStatus, setOrderStatus] = useState(null);

  // Items grouped by category, both the categories and the items within them
  // kept in the menu's saved order.
  const grouped = useMemo(() => {
    const map = new Map();
    menuItems.forEach(item => {
      const cat = item.category || UNCATEGORIZED;
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat).push(item);
    });
    return [...map.entries()];
  }, [menuItems]);

  const sensors = useSensors(
    // A small drag threshold keeps taps on Edit/Delete from starting a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const persistOrder = async (ordered) => {
    setMenuItems(ordered);
    setOrderStatus('saving');
    try {
      const r = await fetch('https://api.reshipesbakery.com/item/reorder', {
        method: 'PUT',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ itemIds: ordered.map(i => i.id) }),
      });
      if (!r.ok) throw new Error();
      setOrderStatus('saved');
      setTimeout(() => setOrderStatus(s => (s === 'saved' ? null : s)), 2000);
    } catch {
      setOrderStatus('failed');
    }
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const type = active.data.current?.type;

    if (type === 'category') {
      if (over.data.current?.type !== 'category') return;
      const names = grouped.map(([cat]) => cat);
      const next = arrayMove(names, names.indexOf(active.data.current.category), names.indexOf(over.data.current.category));
      const byCat = Object.fromEntries(grouped);
      persistOrder(next.flatMap(cat => byCat[cat]));
      return;
    }

    // Items only move within their own category; changing an item's category
    // is what the Edit form is for.
    const category = active.data.current?.category;
    if (over.data.current?.type !== 'item' || category !== over.data.current?.category) return;
    const next = grouped.map(([cat, items]) => {
      if (cat !== category) return [cat, items];
      const ids = items.map(i => i.id);
      return [cat, arrayMove(items, ids.indexOf(active.data.current.itemId), ids.indexOf(over.data.current.itemId))];
    });
    persistOrder(next.flatMap(([, items]) => items));
  };

  useEffect(() => {
    const auth = { headers: { Authorization: `Bearer ${token}` } };

    fetchList('https://api.reshipesbakery.com/reshipes/getMenuTypes', auth)
      .then(setMenuTypes)
      .catch(e => setError(e.message));

    fetchList('https://api.reshipesbakery.com/reshipes/getCategoryTypes', auth)
      .then(setCategories)
      .catch(e => setError(e.message));
  }, [token]);

  useEffect(() => {
    if (!selectedMenu) return;
    setLoadingItems(true);
    setMenuItems([]);
    setSelectedItem(null);
    setAction(pendingAction.current);
    pendingAction.current = null;
    setError(null);
    setSuccess(null);
    fetchList(`https://api.reshipesbakery.com/item/getItemsWithSpecificMenu?menuName=${encodeURIComponent(selectedMenu)}`)
      .then(data => { setMenuItems(data); setLoadingItems(false); })
      .catch(() => { setError('Failed to load items.'); setLoadingItems(false); });
  }, [selectedMenu]);

  const reset = () => { setError(null); setSuccess(null); };

  // ── NEW MENU ───────────────────────────────────────────────────────────────
  const openMenuCreator = () => {
    setNewMenuName('');
    setMenuError(null);
    setCreatingMenu(true);
  };

  const cancelMenuCreator = () => {
    setCreatingMenu(false);
    setNewMenuName('');
    setMenuError(null);
  };

  const createMenu = () => {
    const name = newMenuName.trim().replace(/\s+/g, ' ');
    if (!name) {
      setMenuError('Enter a menu name.');
      return;
    }
    if (allMenus.some(m => m.trim().toLowerCase() === name.toLowerCase())) {
      setMenuError('A menu with that name already exists.');
      return;
    }
    setDraftMenus(prev => [...prev, name]);
    pendingAction.current = 'add';
    setSelectedMenu(name);
    setCreatingMenu(false);
    setNewMenuName('');
    setMenuError(null);
  };

  const authHeaders = { Authorization: `Bearer ${token}` };

  // ── ADD ────────────────────────────────────────────────────────────────────
  const handleAddChange = e => setAddForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleAddSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    reset();
    try {
      // 1. Create the item (use copied URL if no file was selected)
      const body = {
        foodName: addForm.foodName,
        size: addForm.size || null,
        price: parseFloat(addForm.price),
        category: addForm.category,
        minQty: parseInt(addForm.minQty, 10) || 1,
        menuType: selectedMenu,
        imageUrl: addForm.imageFile ? null : addForm.imageUrl,
      };
      const r = await fetch('https://api.reshipesbakery.com/item/add', {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const d = await r.json();
        throw new Error(d.detail || 'Failed to add item.');
      }
      let item = await r.json();

      // 2. Upload image file if one was selected
      if (addForm.imageFile) {
        const fd = new FormData();
        fd.append('file', addForm.imageFile);
        const imgR = await fetch(`https://api.reshipesbakery.com/item/${item.id}/image`, {
          method: 'POST',
          headers: authHeaders,
          body: fd,
        });
        if (!imgR.ok) throw new Error('Item added but image upload failed.');
        item = await imgR.json();
      }

      setMenuItems(prev => [...prev, item]);
      // First item saved: the menu now exists on the server.
      if (isDraftMenu) {
        setMenuTypes(prev => (prev.includes(selectedMenu) ? prev : [...prev, selectedMenu]));
        setDraftMenus(prev => prev.filter(m => m !== selectedMenu));
      }
      setSuccess(isDraftMenu ? `Item added — "${selectedMenu}" menu created!` : 'Item added successfully!');
      setAddForm({ foodName: '', size: '', price: '', category: '', minQty: '1', imageUrl: null, imageFile: null });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── EDIT ───────────────────────────────────────────────────────────────────
  const handleSelectEdit = item => {
    setSelectedItem(item);
    setEditForm({
      foodName: item.foodName,
      size: item.size || '',
      price: String(item.price),
      category: item.category || '',
      minQty: String(item.minQty || 1),
      imageUrl: item.imageUrl || null,
      imageFile: null,
      imagePendingDelete: false,
    });
    reset();
  };

  const handleEditChange = e => setEditForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleEditSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    reset();
    try {
      let finalImageUrl = editForm.imageUrl;

      // 1. Upload new file (backend also deletes old S3 object if unshared)
      if (editForm.imageFile) {
        const fd = new FormData();
        fd.append('file', editForm.imageFile);
        const imgR = await fetch(`https://api.reshipesbakery.com/item/${selectedItem.id}/image`, {
          method: 'POST',
          headers: authHeaders,
          body: fd,
        });
        if (!imgR.ok) throw new Error('Image upload failed.');
        finalImageUrl = (await imgR.json()).imageUrl;
      }
      // 2. Delete image (backend deletes S3 object if unshared)
      else if (editForm.imagePendingDelete) {
        const delR = await fetch(`https://api.reshipesbakery.com/item/${selectedItem.id}/image`, {
          method: 'DELETE',
          headers: authHeaders,
        });
        if (!delR.ok) throw new Error('Image deletion failed.');
        finalImageUrl = null;
      }

      // 3. Save other fields
      const body = {
        foodName: editForm.foodName,
        size: editForm.size || null,
        price: parseFloat(editForm.price),
        category: editForm.category || null,
        minQty: parseInt(editForm.minQty, 10) || 1,
        imageUrl: finalImageUrl,
      };
      const r = await fetch(`https://api.reshipesbakery.com/item/updateItem/${selectedItem.id}`, {
        method: 'PUT',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'Failed to update item.');
      }
      const updated = await r.json();
      // Match on the edited row's id: the server may return a different id
      // when the edit merges into a previously deleted item.
      setMenuItems(prev => prev.map(i => (i.id === selectedItem.id ? updated : i)));
      setSuccess('Item updated successfully!');
      setSelectedItem(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── DELETE ─────────────────────────────────────────────────────────────────
  const handleDelete = async item => {
    if (!(await confirm(`Delete "${itemLabel(item)}"?`))) return;
    setLoading(true);
    reset();
    try {
      const r = await fetch(`https://api.reshipesbakery.com/item/removeItem?itemId=${item.id}`, {
        method: 'DELETE',
        headers: authHeaders,
      });
      if (!r.ok) {
        if (r.status === 401 || r.status === 403) throw new Error('Your session expired. Please log in again.');
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'Failed to delete item.');
      }
      setMenuItems(prev => prev.filter(i => i.id !== item.id));
      setSuccess('Item deleted.');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const showItemList = (action === 'edit' || action === 'delete') && !selectedItem;

  return (
    <div className="container mt-5" style={{ maxWidth: 680 }}>
      {confirmDialog}
      <h1 className="mb-4">Manage Menus &amp; Items</h1>

      {/* ── Menu selector ─────────────────────────────────────────── */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="card-title mb-0">Select a Menu</h5>
            {!creatingMenu && (
              <button className="btn btn-sm btn-outline-primary" onClick={openMenuCreator}>
                + New Menu
              </button>
            )}
          </div>

          {creatingMenu ? (
            <div>
              <label htmlFor="newMenuName">New menu name</label>
              <input
                id="newMenuName"
                className="form-control"
                value={newMenuName}
                autoFocus
                placeholder="e.g. Chanukah"
                onChange={e => { setNewMenuName(e.target.value); setMenuError(null); }}
                onKeyDown={e => {
                  if (e.key === 'Enter') { e.preventDefault(); createMenu(); }
                  if (e.key === 'Escape') cancelMenuCreator();
                }}
              />
              {menuError && <div className="text-danger small mt-1">{menuError}</div>}
              <div className="d-flex gap-2 mt-2">
                <button className="btn btn-success btn-sm" onClick={createMenu}>Create</button>
                <button className="btn btn-outline-secondary btn-sm" onClick={cancelMenuCreator}>Cancel</button>
              </div>
              <small className="text-muted d-block mt-2">
                The menu is saved once you add its first item.
              </small>
            </div>
          ) : (
            <select className="form-control" value={selectedMenu} onChange={e => setSelectedMenu(e.target.value)}>
              <option value="">— Choose a menu —</option>
              {allMenus.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          )}
        </div>
      </div>

      {isDraftMenu && (
        <div className="alert alert-info">
          <strong>"{selectedMenu}"</strong> is a new menu. Add its first item below to save it.
        </div>
      )}

      {selectedMenu && (
        <>
          {/* ── Action tabs ───────────────────────────────────────── */}
          <div className="btn-group w-100 mb-4" role="group">
            {['add', 'edit', 'delete'].map(a => (
              <button
                key={a}
                className={`btn btn-${action === a ? actionColor(a) : 'outline-' + actionColor(a)}`}
                onClick={() => { setAction(a); setSelectedItem(null); reset(); }}
              >
                {actionLabel(a)}
              </button>
            ))}
          </div>

          {error && <div className="alert alert-danger">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          {loadingItems && <p>Loading items...</p>}

          {/* ── Item list (for edit / delete) ─────────────────────── */}
          {!loadingItems && showItemList && (
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <h6 className="text-muted mb-0">
                    {action === 'edit' ? 'Choose an item to edit:' : 'Choose an item to delete:'}
                  </h6>
                  {orderStatus === 'saving' && <small className="text-muted">Saving order…</small>}
                  {orderStatus === 'saved' && <small className="text-success">Order saved</small>}
                  {orderStatus === 'failed' && <small className="text-danger">Couldn't save order</small>}
                </div>
                {menuItems.length === 0 ? (
                  <p className="text-muted mt-3">No items in this menu.</p>
                ) : (
                  <>
                    <p className="text-muted small mb-3">
                      Drag <span aria-hidden="true">⠿</span> to reorder an item, or a category heading to move
                      the whole section. This is the order customers see when they order.
                    </p>
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                      <SortableContext
                        items={grouped.map(([cat]) => `cat-${cat}`)}
                        strategy={verticalListSortingStrategy}
                      >
                        {grouped.map(([cat, items]) => (
                          <SortableRow
                            key={cat}
                            id={`cat-${cat}`}
                            data={{ type: 'category', category: cat }}
                            className="mb-4"
                          >
                            {({ handleProps }) => (
                              <>
                                <div
                                  className="d-flex align-items-center gap-2 pb-1 mb-2"
                                  style={{ borderBottom: '1px solid #e9dfd3' }}
                                >
                                  <DragHandle label={`Move ${cat} section`} {...handleProps} />
                                  <strong>{cat}</strong>
                                  <span className="text-muted small">({items.length})</span>
                                </div>
                                <SortableContext
                                  items={items.map(i => `item-${i.id}`)}
                                  strategy={verticalListSortingStrategy}
                                >
                                  <div className="list-group">
                                    {items.map(item => (
                                      <SortableRow
                                        key={item.id}
                                        id={`item-${item.id}`}
                                        data={{ type: 'item', category: cat, itemId: item.id }}
                                        className="list-group-item d-flex justify-content-between align-items-center"
                                      >
                                        {({ handleProps: itemHandle }) => (
                                          <>
                                            <div className="d-flex align-items-center gap-2">
                                              <DragHandle label={`Move ${item.foodName}`} {...itemHandle} />
                                              {item.imageUrl ? (
                                                <img src={item.imageUrl} alt={item.foodName}
                                                  style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 6 }} />
                                              ) : (
                                                <div style={{ width: 36, height: 36, borderRadius: 6, background: '#f0e6db', flexShrink: 0 }} />
                                              )}
                                              <span>{itemLabel(item)}</span>
                                            </div>
                                            <button
                                              className={`btn btn-sm btn-outline-${actionColor(action)}`}
                                              onClick={() => action === 'delete' ? handleDelete(item) : handleSelectEdit(item)}
                                              disabled={loading}
                                            >
                                              {action === 'delete' ? 'Delete' : 'Edit'}
                                            </button>
                                          </>
                                        )}
                                      </SortableRow>
                                    ))}
                                  </div>
                                </SortableContext>
                              </>
                            )}
                          </SortableRow>
                        ))}
                      </SortableContext>
                    </DndContext>
                  </>
                )}
              </div>
            </div>
          )}

          {/* ── ADD form ──────────────────────────────────────────── */}
          {action === 'add' && (
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">Add Item to "{selectedMenu}"</h5>
                <form onSubmit={handleAddSubmit}>
                  <div className="form-group mb-3">
                    <label>Food Name</label>
                    <input className="form-control" name="foodName" value={addForm.foodName}
                      onChange={handleAddChange} required />
                  </div>
                  <div className="form-group mb-3">
                    <label>Size <small className="text-muted">(optional)</small></label>
                    <input className="form-control" name="size" value={addForm.size}
                      onChange={handleAddChange} placeholder="e.g. Small, Large" />
                  </div>
                  <div className="form-group mb-3">
                    <label>Price ($)</label>
                    <input className="form-control" type="number" step="0.01" min="0.01"
                      name="price" value={addForm.price} onChange={handleAddChange} required />
                  </div>
                  <div className="form-group mb-3">
                    <label>Minimum quantity <small className="text-muted">(1 = no minimum)</small></label>
                    <input className="form-control" type="number" step="1" min="1"
                      name="minQty" value={addForm.minQty} onChange={handleAddChange} required />
                  </div>
                  <div className="form-group mb-3">
                    <label>Category</label>
                    <select className="form-control" name="category" value={addForm.category}
                      onChange={handleAddChange} required>
                      <option value="">Select Category</option>
                      {categories.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <ImagePicker
                    imageUrl={addForm.imageUrl}
                    imageFile={addForm.imageFile}
                    pendingDelete={false}
                    onFileSelect={file => setAddForm(f => ({ ...f, imageFile: file, imageUrl: null }))}
                    onUrlSelect={url => setAddForm(f => ({ ...f, imageUrl: url, imageFile: null }))}
                    onDelete={() => setAddForm(f => ({ ...f, imageUrl: null, imageFile: null }))}
                    onUndoDelete={() => {}}
                    menuTypes={menuTypes}
                  />
                  <button className="btn btn-success" type="submit" disabled={loading}>
                    {loading ? 'Adding...' : 'Add Item'}
                  </button>
                </form>
              </div>
            </div>
          )}

          {/* ── EDIT form ─────────────────────────────────────────── */}
          {action === 'edit' && selectedItem && (
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">Editing: {itemLabel(selectedItem)}</h5>
                <form onSubmit={handleEditSubmit}>
                  <div className="form-group mb-3">
                    <label>Food Name</label>
                    <input className="form-control" name="foodName" value={editForm.foodName}
                      onChange={handleEditChange} required />
                  </div>
                  <div className="form-group mb-3">
                    <label>Size <small className="text-muted">(optional)</small></label>
                    <input className="form-control" name="size" value={editForm.size}
                      onChange={handleEditChange} placeholder="e.g. Small, Large" />
                  </div>
                  <div className="form-group mb-3">
                    <label>Price ($)</label>
                    <input className="form-control" type="number" step="0.01" min="0.01"
                      name="price" value={editForm.price} onChange={handleEditChange} required />
                  </div>
                  <div className="form-group mb-3">
                    <label>Minimum quantity <small className="text-muted">(1 = no minimum)</small></label>
                    <input className="form-control" type="number" step="1" min="1"
                      name="minQty" value={editForm.minQty} onChange={handleEditChange} required />
                  </div>
                  <div className="form-group mb-3">
                    <label>Category</label>
                    <select className="form-control" name="category" value={editForm.category}
                      onChange={handleEditChange} required>
                      <option value="">Select Category</option>
                      {categories.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <ImagePicker
                    imageUrl={editForm.imageUrl}
                    imageFile={editForm.imageFile}
                    pendingDelete={editForm.imagePendingDelete}
                    onFileSelect={file => setEditForm(f => ({ ...f, imageFile: file, imageUrl: f.imageUrl, imagePendingDelete: false }))}
                    onUrlSelect={url => setEditForm(f => ({ ...f, imageUrl: url, imageFile: null, imagePendingDelete: false }))}
                    onDelete={() => setEditForm(f => ({ ...f, imagePendingDelete: true, imageFile: null }))}
                    onUndoDelete={() => setEditForm(f => ({ ...f, imagePendingDelete: false, imageUrl: selectedItem.imageUrl || null }))}
                    menuTypes={menuTypes}
                  />
                  <div className="d-flex gap-2">
                    <button className="btn btn-warning" type="submit" disabled={loading}>
                      {loading ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button className="btn btn-outline-secondary" type="button"
                      onClick={() => setSelectedItem(null)}>
                      Back
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ManageMenu;
